import { useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ScanLine,
  Gem,
  Receipt,
  Users,
  Gavel,
  LogOut,
  Activity,
  Coins,
  Wallet,
  ShoppingBag,
  Building2,
  ShieldAlert,
  FileWarning,
  Globe,
  ChevronsLeft,
  ChevronsRight,
  UserCog,
  BadgeCheck,
  HandCoins,
  Layout as LayoutIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "staff", "accountant", "sales"] },
  { to: "/pos", label: "POS", icon: ScanLine, roles: ["admin", "staff", "accountant", "sales"] },
  { to: "/inventory", label: "Inventory", icon: Gem, roles: ["admin", "staff", "accountant", "sales"] },
  { to: "/accounting", label: "Accounting", icon: Receipt, roles: ["admin", "accountant", "staff"] },
  { to: "/my-sales", label: "My Sales", icon: ShoppingBag, roles: ["sales"] },
  { to: "/crm", label: "CRM", icon: Users, roles: ["admin", "staff", "accountant", "sales"] },
  { to: "/auctions", label: "Auctions", icon: Gavel, roles: ["admin", "staff"] },
  { to: "/memos", label: "Memos", icon: FileWarning, roles: ["admin", "staff"] },
  { to: "/borrowings", label: "Borrowings", icon: HandCoins, roles: ["admin", "staff"] },
  { to: "/website", label: "Website", icon: Globe, roles: ["admin"] },
  { to: "/user-warehouses", label: "User Warehouses", icon: Building2, roles: ["admin"] },
  { to: "/commissions", label: "Commissions", icon: Coins, roles: ["admin", "sales"] },
  { to: "/payroll", label: "Payroll", icon: Wallet, roles: ["admin", "accountant"] },
  { to: "/hr", label: "HR", icon: BadgeCheck, roles: ["admin"] },
  { to: "/audit", label: "Audit", icon: Activity, roles: ["admin"] },
  { to: "/super-admin", label: "Super Admin", icon: ShieldAlert, roles: ["admin"], superOnly: true },
  { to: "/label-designer", label: "Label Designer", icon: LayoutIcon, roles: ["admin"], superOnly: true },
];

export default function Sidebar({ onNavigate, collapsed = false, onToggleCollapse = null }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const visibleNav = useMemo(
    () => NAV.filter((n) => {
      if (!user?.role) return false;
      if (!n.roles.includes(user.role)) return false;
      if (n.superOnly && !user.is_super_admin) return false;
      return true;
    }),
    [user?.role, user?.is_super_admin],
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className={`${collapsed ? "w-16" : "w-64"} h-screen max-h-screen bg-[#0A0A0A] border-r border-white/5 flex flex-col transition-[width] duration-200 ease-out`}
    >
      {/* Brand — shrinks on short viewports (≤700px tall) so more nav items fit before scrolling */}
      <div className={`${collapsed ? "px-2 py-4" : "px-6 py-6 [@media(max-height:700px)]:py-3"} border-b border-white/5 flex items-center justify-center shrink-0`}>
        {collapsed ? (
          // Compact mark — small logo or first letter of brand
          <img
            src="/brand/opikjewels-logo-v2.png"
            alt="Opik Jewels"
            className="h-9 w-auto object-contain opacity-80"
            data-testid="sidebar-brand-logo"
          />
        ) : (
          <img
            src="/brand/opikjewels-logo-v2.png"
            alt="Opik Jewels"
            className="h-[70px] [@media(max-height:700px)]:h-12 w-auto object-contain transition-[height] duration-200"
            data-testid="sidebar-brand-logo"
          />
        )}
      </div>

      {/* Nav (scrollable when items exceed viewport height — keeps brand + user-card pinned) */}
      <nav className={`flex-1 min-h-0 overflow-y-auto ${collapsed ? "px-2 py-3" : "px-3 py-3"} space-y-0.5`}>
        {visibleNav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={onNavigate}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                [
                  collapsed
                    ? "group flex items-center justify-center py-2.5 transition-all relative"
                    : "group flex items-center gap-3 px-4 py-2.5 text-sm tracking-wide transition-all relative",
                  isActive
                    ? "text-[#D4AF37] bg-[#D4AF37]/5"
                    : "text-[#8A8A8A] hover:text-[#EAEAEA]",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-0 bottom-0 w-px bg-[#D4AF37]" />
                  )}
                  <Icon className="w-4 h-4" strokeWidth={1.25} />
                  {!collapsed && (
                    <span className="uppercase text-[0.72rem] tracking-[0.18em]">{item.label}</span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Collapse toggle (PC only — Layout passes a handler from a `hidden lg:block` wrapper) */}
      {onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          data-testid="sidebar-collapse-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="hidden lg:flex items-center justify-center gap-2 py-2.5 mx-3 mb-2 border border-white/10 hover:border-[#D4AF37]/40 text-[#8A8A8A] hover:text-[#D4AF37] transition-colors"
        >
          {collapsed ? (
            <ChevronsRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          ) : (
            <>
              <ChevronsLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="text-[0.6rem] uppercase tracking-[0.18em]">Collapse</span>
            </>
          )}
        </button>
      )}

      {/* User card */}
      <div className={`${collapsed ? "px-2 py-3" : "px-5 py-5 [@media(max-height:700px)]:py-3"} border-t border-white/5 shrink-0`}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/profile")}
              data-testid="open-profile-collapsed"
              className="w-9 h-9 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] text-xs mono hover:bg-[#D4AF37]/10 transition-colors"
              title={`${user?.name || ""} · ${user?.role || ""} — Open profile`}
            >
              {user?.name?.[0]?.toUpperCase() || "?"}
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              title="Sign out"
              className="w-full flex items-center justify-center py-2 btn-outline-gold"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.25} />
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => navigate("/profile")}
              data-testid="open-profile"
              className="w-full flex items-center gap-3 mb-4 [@media(max-height:700px)]:mb-2 group text-left hover:bg-white/[0.02] -mx-2 px-2 py-1 transition-colors"
              title="Open my profile"
            >
              <div className="w-9 h-9 [@media(max-height:700px)]:w-7 [@media(max-height:700px)]:h-7 rounded-none border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] text-xs mono group-hover:bg-[#D4AF37]/10 transition-colors">
                {user?.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#EAEAEA] truncate" data-testid="user-name">{user?.name}</div>
                <div className="overline text-[0.58rem]" data-testid="user-role">{user?.role}</div>
              </div>
              <UserCog className="w-3.5 h-3.5 text-[#666] group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
            </button>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2.5 [@media(max-height:700px)]:py-1.5 btn-outline-gold"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.25} />
              <span>Sign out</span>
            </button>
          </>
        )}
      </div>
    </aside>
  );
}
